import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PriceTag } from "@/components/PriceTag";
import { CreditCard, Lock, ShieldCheck } from "lucide-react";

function formatCardNumber(v) {
  return v
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();
}
function formatExpiry(v) {
  const d = v.replace(/\D/g, "").slice(0, 4);
  if (d.length < 3) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

export default function Checkout() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const planCode = params.get("plan");
  const nav = useNavigate();
  const [svc, setSvc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [card, setCard] = useState({
    number: "4242 4242 4242 4242",
    holder: "",
    expiry: "12/29",
    cvc: "123",
  });

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setSvc(r.data));
  }, [slug]);

  if (!svc) {
    return <div className="container-pad py-24 font-mono text-zinc-500">LOADING…</div>;
  }
  const plan = svc.plans.find((p) => p.code === planCode) || svc.plans[0];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        service_id: svc.id,
        plan_code: plan.code,
        card_number: card.number,
        card_holder: card.holder,
        card_expiry: card.expiry,
        card_cvc: card.cvc,
      });
      nav(`/orders/${data.order_id}`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="container-pad py-12 md:py-20">
      <div className="overline">// CHECKOUT · BRIDGE GATEWAY</div>
      <h1 className="mt-3 font-display text-4xl tracking-tight">
        Pay & <span className="text-signal">provision</span>
      </h1>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* CARD FORM */}
        <form onSubmit={submit} className="md:col-span-7 card-flat p-6 md:p-8" data-testid="checkout-form">
          <div className="flex items-center justify-between border-b border-zinc-800 pb-4 mb-6">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-signal" />
              <span className="font-mono text-xs tracking-widest text-white">CARD DETAILS</span>
            </div>
            <span className="badge-mono">SECURE · TOKENIZED</span>
          </div>

          <div className="space-y-5">
            <div>
              <label className="label-flat">CARD NUMBER</label>
              <input
                data-testid="card-number-input"
                inputMode="numeric"
                required
                value={card.number}
                onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })}
                className="input-flat tracking-widest"
                placeholder="4242 4242 4242 4242"
              />
            </div>
            <div>
              <label className="label-flat">CARDHOLDER NAME</label>
              <input
                data-testid="card-holder-input"
                required
                value={card.holder}
                onChange={(e) => setCard({ ...card, holder: e.target.value })}
                className="input-flat"
                placeholder="ADA LOVELACE"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-flat">EXPIRY (MM/YY)</label>
                <input
                  data-testid="card-expiry-input"
                  required
                  value={card.expiry}
                  onChange={(e) => setCard({ ...card, expiry: formatExpiry(e.target.value) })}
                  className="input-flat"
                  placeholder="12/29"
                />
              </div>
              <div>
                <label className="label-flat">CVC</label>
                <input
                  data-testid="card-cvc-input"
                  inputMode="numeric"
                  required
                  value={card.cvc}
                  onChange={(e) => setCard({ ...card, cvc: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  className="input-flat"
                  placeholder="123"
                />
              </div>
            </div>
          </div>

          {err && (
            <div
              data-testid="checkout-error"
              className="mt-5 border border-red-900/60 bg-red-950/30 text-red-300 text-sm p-3 rounded-sm font-mono"
            >
              {err}
            </div>
          )}

          <button
            disabled={busy}
            data-testid="pay-button"
            className="btn-signal w-full h-12 mt-6 text-sm font-mono tracking-widest disabled:opacity-50"
          >
            <Lock size={14} /> {busy ? "PROCESSING…" : `PAY $${plan.price_usd.toFixed(2)}`}
          </button>

          <div className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <ShieldCheck size={12} className="text-signal" /> Card data tokenized at the bridge — never stored.
          </div>

          <div className="mt-6 card-flat p-3 font-mono text-[11px] leading-5 text-zinc-500">
            <span className="text-zinc-300">// TEST CARDS</span><br />
            ✓ 4242 4242 4242 4242 → approved<br />
            ✗ 4000 0000 0000 0002 → declined<br />
            ✗ 4000 0000 0000 0341 → insufficient funds
          </div>
        </form>

        {/* ORDER SUMMARY */}
        <aside className="md:col-span-5">
          <div className="card-flat sticky top-24">
            <div className="border-b border-zinc-800 p-5">
              <div className="overline">// ORDER SUMMARY</div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="badge-mono mb-2">{svc.subtype === "dedicated" ? "DEDICATED" : "VPS"}</div>
                <div className="font-display text-xl">{svc.name}</div>
                <div className="text-sm text-zinc-400 mt-1">{plan.name} · {plan.quota || ""}</div>
              </div>
              <div className="border-t border-zinc-800 pt-4 flex items-center justify-between">
                <span className="overline">SUBTOTAL</span>
                <PriceTag amount={plan.price_usd} period={plan.period === "month" ? "mo" : plan.period} />
              </div>
              <div className="flex items-center justify-between">
                <span className="overline">TAXES</span>
                <span className="font-mono text-zinc-400 text-sm">CALCULATED AT GATEWAY</span>
              </div>
              <div className="border-t border-zinc-800 pt-4 flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-widest text-white">TOTAL DUE</span>
                <PriceTag amount={plan.price_usd} period={plan.period === "month" ? "mo" : plan.period} />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
